import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Globe, UserPlus, X, Mail, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { containersApi, formatError } from "../lib/api";

const VisibilityPill = ({ active, icon: Icon, label, desc, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-left rounded-2xl p-4 border-2 transition-all flex-1 ${
      active
        ? "border-[#277A82] bg-[#DCEDF0]/50"
        : "border-[#E2E0D8] bg-white hover:border-[#277A82]/60"
    }`}
  >
    <div className="flex items-center gap-2">
      <Icon className={`w-4 h-4 ${active ? "text-[#1F5566]" : "text-[#8A93A6]"}`} />
      <span className={`font-semibold text-sm ${active ? "text-[#1F5566]" : "text-[#2D3142]"}`}>
        {label}
      </span>
    </div>
    <p className="text-[11px] text-[#4F5D75] mt-2 leading-snug">{desc}</p>
  </button>
);

export const SharingPanel = ({ container, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState("");

  const visibility = container.visibility || "shared";
  const editors = container.editors || [];

  const callSharing = async (data) => {
    setBusy(true);
    try {
      const updated = await containersApi.updateSharing(container.id, data);
      onUpdated?.(updated);
      return updated;
    } catch (err) {
      toast.error(formatError(err));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const setVisibility = async (v) => {
    if (v === visibility) return;
    const r = await callSharing({ visibility: v });
    if (r) toast.success(v === "private" ? "Now private" : "Now shared");
  };

  const addEditor = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    const r = await callSharing({ add_editor_email: email.trim() });
    if (r) {
      toast.success("Editor added");
      setEmail("");
      setShowInvite(false);
    }
  };

  const removeEditor = async (userId) => {
    const r = await callSharing({ remove_editor_id: userId });
    if (r) toast.success("Editor removed");
  };

  return (
    <div className="tup-card p-6 space-y-4" data-testid="sharing-panel">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
        <Users className="w-3.5 h-3.5" /> Sharing
      </div>

      <div className="flex gap-3" data-testid="sharing-visibility-toggle">
        <VisibilityPill
          active={visibility === "shared"}
          icon={Globe}
          label="Shared"
          desc="Anyone with the QR can view. Add editors below."
          onClick={() => !busy && setVisibility("shared")}
          testid="sharing-shared-btn"
        />
        <VisibilityPill
          active={visibility === "private"}
          icon={Lock}
          label="Private"
          desc="Only your household sees this. Outside scanners get locked out."
          onClick={() => !busy && setVisibility("private")}
          testid="sharing-private-btn"
        />
      </div>

      {visibility === "shared" && (
        <div className="pt-2 border-t border-[#E2E0D8]" data-testid="sharing-editors-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
                Outside editors
              </div>
              <div className="text-xs text-[#4F5D75] mt-0.5">
                Specific people (not in your household) who can update this container.
              </div>
            </div>
            <Button
              onClick={() => setShowInvite((v) => !v)}
              data-testid="sharing-invite-toggle-btn"
              variant="outline"
              size="sm"
              className="border-[#277A82] text-[#277A82] hover:bg-[#DCEDF0]"
            >
              <UserPlus className="w-3.5 h-3.5 mr-1.5" />
              {showInvite ? "Cancel" : "Add"}
            </Button>
          </div>

          <AnimatePresence>
            {showInvite && (
              <motion.form
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                onSubmit={addEditor}
                className="overflow-hidden"
              >
                <div className="flex gap-2 mb-3">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="their-email@example.com"
                    data-testid="sharing-invite-email-input"
                    className="bg-white border-[#E2E0D8]"
                    autoFocus
                  />
                  <Button
                    type="submit"
                    disabled={busy || !email.trim()}
                    data-testid="sharing-invite-submit-btn"
                    className="brand-gradient-bg text-white shrink-0"
                  >
                    <Mail className="w-4 h-4" />
                  </Button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>

          <ul className="space-y-2" data-testid="sharing-editors-list">
            {editors.length === 0 ? (
              <li className="text-xs text-[#8A93A6] italic" data-testid="sharing-no-editors">
                No outside editors yet. By default, outside scanners can only view.
              </li>
            ) : (
              editors.map((ed) => (
                <li
                  key={ed.id}
                  className="flex items-center justify-between bg-[#F5F3ED] rounded-xl px-3 py-2.5"
                  data-testid={`sharing-editor-${ed.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-[#2D3142] truncate">{ed.name}</div>
                    <div className="text-xs text-[#8A93A6] truncate">{ed.email}</div>
                  </div>
                  <button
                    onClick={() => removeEditor(ed.id)}
                    disabled={busy}
                    data-testid={`sharing-remove-${ed.id}`}
                    className="text-[#9C3E1D] hover:bg-[#FBEAE5] rounded-lg p-1.5 shrink-0"
                    aria-label="Remove editor"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
